import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { motion } from 'framer-motion';
import { FaTrophy, FaStream, FaChartLine, FaTwitch, FaYoutube, FaTwitter, FaDiscord, FaTiktok, FaInstagram, FaDna, FaUserFriends } from 'react-icons/fa';
import TierIcon from '../components/TierIcon.js';
import TierUpgrade from '../components/TierUpgrade.js';

const Profile = () => {
  const { username } = useParams();
  const { currentUser, userDbData, loading, authInitialized } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Wait for profile data to load
  useEffect(() => {
    if (!loading && authInitialized) {
      // Simulate profile data loading
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [loading, authInitialized]);

  const isOwnProfile = !username || (currentUser && userDbData?.username === username);
  
  // Safe user data access
  const userData = userDbData || {};
  const displayName = userData?.displayName || userData?.username || currentUser?.displayName || currentUser?.email || 'User';
  const photoURL = userData?.photoURL || currentUser?.photoURL || null;

  // Mock data - replace with actual data from your backend
  const profileData = {
    bio: "Professional esports player and content creator. Specializing in FPS games and competitive tournaments.",
    teams: [
      { id: 1, name: "Team Alpha", role: "Captain", members: 5, logo: "https://api.dicebear.com/7.x/shapes/svg?seed=TeamAlpha" },
      { id: 2, name: "StreamerWars Elite", role: "Member", members: 8, logo: "https://api.dicebear.com/7.x/shapes/svg?seed=Elite" }
    ],
    tier: 'STANDARD',
    gamesStreamed: [
      { id: 1, name: "Valorant", hours: 156, category: "FPS", lastPlayed: "2024-03-15", thumbnail: "/game-thumbnails/valorant.jpg" },
      { id: 2, name: "League of Legends", hours: 98, category: "MOBA", lastPlayed: "2024-03-14", thumbnail: "/game-thumbnails/lol.jpg" },
      { id: 3, name: "CS:GO", hours: 234, category: "FPS", lastPlayed: "2024-03-13", thumbnail: "/game-thumbnails/csgo.jpg" }
    ],
    dnaScore: 850,
    achievements: [
      { id: 1, title: "Tournament Victor", description: "Won 10 tournaments", icon: "🏆" },
      { id: 2, title: "Stream Master", description: "1000+ hours streamed", icon: "🎮" },
      { id: 3, title: "Community Leader", description: "Built a community of 10k+ followers", icon: "👥" }
    ],
    socialLinks: {
      twitch: 'twitch.tv/streamerwarz',
      youtube: 'youtube.com/streamerwarz',
      twitter: 'twitter.com/streamerwarz',
      discord: 'discord.gg/streamerwarz',
      tiktok: 'tiktok.com/@streamerwarz',
      instagram: 'instagram.com/streamerwarz'
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'twitch': return { icon: FaTwitch, color: '#9146FF' };
      case 'youtube': return { icon: FaYoutube, color: '#FF0000' };
      case 'twitter': return { icon: FaTwitter, color: '#1DA1F2' };
      case 'discord': return { icon: FaDiscord, color: '#7289DA' };
      case 'tiktok': return { icon: FaTiktok, color: '#000000' };
      case 'instagram': return { icon: FaInstagram, color: '#E1306C' };
      default: return null;
    }
  };

  // Show loading state while profile data is loading
  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-harmony-blue pt-14 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-harmony-orange"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-harmony-blue pt-14 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Profile Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-harmony-dark rounded-lg p-6"
        >
          <div className="flex items-start space-x-6">
            <div className="w-24 h-24 bg-harmony-orange/20 rounded-full flex items-center justify-center">
              {photoURL ? (
                <img src={photoURL} alt="Profile" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-4xl text-harmony-orange">{displayName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-3xl font-bold text-harmony-white">{displayName}</h1>
                    <TierIcon tier={profileData.tier || 'STANDARD'} size="lg" />
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="px-2 py-1 bg-harmony-orange/20 text-harmony-orange rounded-full text-sm">
                      {profileData.tier || 'Standard'} Tier
                    </span>
                    <span className="flex items-center text-harmony-white/60">
                      <FaDna className="mr-1 text-harmony-orange" />
                      DNA Score: {profileData.dnaScore}
                    </span>
                    <span className="text-harmony-white/60">Member since 2024</span>
                  </div>
                </div>
                {!isOwnProfile && (
                  <div className="flex space-x-4">
                    <button className="px-4 py-2 bg-harmony-orange text-harmony-white rounded-lg hover:bg-opacity-90 transition-colors">
                      Follow
                    </button>
                    <button className="px-4 py-2 border border-harmony-orange/20 text-harmony-white rounded-lg hover:bg-harmony-orange/10 transition-colors">
                      Message
                    </button>
                  </div>
                )}
              </div>

              {/* Bio */}
              <div className="mt-4">
                <p className="text-harmony-white/80">{profileData.bio}</p>
              </div>

              <div className="mt-4 grid grid-cols-4 gap-4">
                <div>
                  <div className="text-2xl font-bold text-harmony-white">1.2K</div>
                  <div className="text-harmony-white/60">Followers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-harmony-white">856</div>
                  <div className="text-harmony-white/60">Following</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-harmony-white">45</div>
                  <div className="text-harmony-white/60">Tournaments</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-harmony-white">{profileData.teams.length}</div>
                  <div className="text-harmony-white/60">Teams</div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-6 flex flex-wrap gap-4">
                {Object.entries(profileData.socialLinks).map(([platform, url]) => {
                  const socialInfo = getSocialIcon(platform);
                  if (!socialInfo) return null;
                  const Icon = socialInfo.icon;
                  
                  return (
                    <a
                      key={platform}
                      href={`https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-harmony-blue/20 rounded-lg hover:bg-harmony-blue/30 transition-colors"
                      style={{ color: socialInfo.color }}
                    >
                      <Icon className="mr-2" />
                      <span className="capitalize">{platform}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Navigation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-harmony-dark rounded-lg overflow-hidden"
        >
          <div className="flex space-x-4 border-b border-harmony-white/10 px-6">
            <button
              className={`py-4 px-4 ${
                activeTab === 'overview'
                  ? 'text-harmony-orange border-b-2 border-harmony-orange'
                  : 'text-harmony-white/60 hover:text-harmony-white'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`py-4 px-4 ${
                activeTab === 'games'
                  ? 'text-harmony-orange border-b-2 border-harmony-orange'
                  : 'text-harmony-white/60 hover:text-harmony-white'
              }`}
              onClick={() => setActiveTab('games')}
            >
              Games
            </button>
            <button
              className={`py-4 px-4 ${
                activeTab === 'teams'
                  ? 'text-harmony-orange border-b-2 border-harmony-orange'
                  : 'text-harmony-white/60 hover:text-harmony-white'
              }`}
              onClick={() => setActiveTab('teams')}
            >
              Teams
            </button>
            <button
              className={`py-4 px-4 ${
                activeTab === 'achievements'
                  ? 'text-harmony-orange border-b-2 border-harmony-orange'
                  : 'text-harmony-white/60 hover:text-harmony-white'
              }`}
              onClick={() => setActiveTab('achievements')}
            >
              Achievements
            </button>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-harmony-blue/20 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-harmony-white mb-4 flex items-center">
                      <FaTrophy className="mr-2 text-harmony-orange" />
                      Tournament Stats
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Wins</span>
                        <span className="text-harmony-orange">32</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Losses</span>
                        <span className="text-harmony-white/60">13</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Win Rate</span>
                        <span className="text-harmony-orange">71%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-harmony-blue/20 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-harmony-white mb-4 flex items-center">
                      <FaStream className="mr-2 text-harmony-orange" />
                      Stream Stats
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Total Streams</span>
                        <span className="text-harmony-orange">156</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Avg. Viewers</span>
                        <span className="text-harmony-orange">1.2K</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Stream Hours</span>
                        <span className="text-harmony-orange">1,234</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-harmony-blue/20 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-harmony-white mb-4 flex items-center">
                      <FaChartLine className="mr-2 text-harmony-orange" />
                      Engagement
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Total Views</span>
                        <span className="text-harmony-orange">50K</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Total Likes</span>
                        <span className="text-harmony-orange">75K</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-harmony-white/60">Comments</span>
                        <span className="text-harmony-orange">1.5K</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Games Tab */}
            {activeTab === 'games' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profileData.gamesStreamed.map(game => (
                    <div key={game.id} className="bg-harmony-blue/20 rounded-lg overflow-hidden">
                      <div className="aspect-video bg-harmony-blue/40">
                        {game.thumbnail && (
                          <img src={game.thumbnail} alt={game.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-harmony-white mb-2">{game.name}</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-harmony-white/60">Hours Streamed</span>
                            <span className="text-harmony-orange">{game.hours}h</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-harmony-white/60">Category</span>
                            <span className="text-harmony-white">{game.category}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-harmony-white/60">Last Played</span>
                            <span className="text-harmony-white">{new Date(game.lastPlayed).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === 'teams' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {profileData.teams.map(team => (
                    <div key={team.id} className="bg-harmony-blue/20 p-4 rounded-lg flex items-center space-x-4">
                      <img src={team.logo} alt={team.name} className="w-16 h-16 rounded-lg" />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-harmony-white">{team.name}</h3>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-harmony-orange text-sm">{team.role}</span>
                          <span className="text-harmony-white/60 text-sm flex items-center">
                            <FaUserFriends className="mr-1" />
                            {team.members} members
                          </span>
                        </div>
                      </div>
                      <Link
                        to={`/teams/${team.id}`}
                        className="px-4 py-2 bg-harmony-orange/20 text-harmony-orange rounded-lg hover:bg-harmony-orange/30 transition-colors"
                      >
                        View Team
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profileData.achievements.map(achievement => (
                    <div key={achievement.id} className="bg-harmony-blue/20 p-4 rounded-lg">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{achievement.icon}</span>
                        <h3 className="text-lg font-semibold text-harmony-white">{achievement.title}</h3>
                      </div>
                      <p className="text-harmony-white/60">{achievement.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 gap-8">
          {isOwnProfile && <TierUpgrade />}
        </div>
      </div>
    </div>
  );
};

export default Profile; 